import { useState } from 'react';
import type {
  AdminAnalyticsResponse,
  AdminAppAnalytics,
  AdminEventIngestResponse,
  AdminInstallFailuresResponse,
  AnalyticsSummary,
  AdoptionEntry,
  AdoptionResponse,
} from '@boardflight/shared';
import { api } from '../api.js';
import { Async, Filter, Modal, Panel, Time, duration, useAsync, useSelection } from '../ui.js';

/**
 * Every game at once, and any one of them (§3).
 *
 * The ranking is the question an admin actually opens the console with — is any of this being used,
 * and by whom — and it is the one thing no per-app screen could answer.
 *
 * What each column is for (2026-10-06): **Boards** is reach — real Boards a BoardFlight desktop app
 * found the game on (`board_apps`, since 2026-10-11) — with how many *kept* it under it: still there
 * on a later day than it arrived, which is the retention a Board's row can state now that nothing
 * ties a play to a Board. Not the layer's install ids — a web game's is new on every launch — and
 * not the addresses plays are keyed on (`routes/analytics.ts`). A game that is not collecting, or
 * that players only ever installed by hand, shows none.
 * **Sessions** carries its trend against the same range moved back by its length — cut at the same
 * time of day while the range runs to today, so a morning is not compared with a whole day —
 * because a number with no direction does not say whether a game is taking off or winding down.
 * **Avg session** is playtime over sessions; the median is in the game's own modal. **Installs** is
 * accounts that installed it, an update not counted — reported whatever the game's analytics switch
 * says, so every game has one — with the failures a Board reported under it, which is where a
 * broken build shows up first.
 *
 * Every range here is UTC days, as the server's are: the server ends a range at today in UTC, and a
 * start in the reader's own day made "Last 7 days" six of them for a reader east of UTC between
 * their midnight and UTC's, and eight for one west of it between UTC's midnight and theirs. The
 * creator dashboard does the same.
 */

/** `YYYY-MM-DD` in UTC — the day the server counts in, whatever the reader's zone. */
const utcDay = (at: number): string => new Date(at).toISOString().slice(0, 10);

/**
 * The picker's ranges, as UTC days back from today: `from` is that many days before today and `to`
 * that many, so Today is today so far and Yesterday is the whole of the day before. The server
 * compares each with the same length just before it, cut at the same time of day only while the
 * range runs to today.
 */
const RANGES = [
  { value: 'today', label: 'Today', from: 0, to: 0 },
  { value: 'yesterday', label: 'Yesterday', from: 1, to: 1 },
  { value: '7', label: 'Last 7 days', from: 6, to: 0 },
  { value: '30', label: 'Last 30 days', from: 29, to: 0 },
  { value: '90', label: 'Last 90 days', from: 89, to: 0 },
] as const;
type RangeValue = (typeof RANGES)[number]['value'];

/**
 * A filter on every number on the tab, never a split of them (owner, 2026-10-28): the totals are
 * the question, and which engine a game is built on is a way to narrow it.
 */
const PLATFORMS = [
  { value: '', label: 'All' },
  { value: 'web', label: 'Web' },
  { value: 'unity', label: 'Unity' },
] as const;

/** "on", "since" or "from … to", for the line under a table, so Yesterday does not read as since. */
function rangeWords(from: string, to: string): string {
  if (from === to) return `on ${from}`;
  return to === utcDay(Date.now()) ? `since ${from}` : `from ${from} to ${to}`;
}

export function Analytics() {
  const [range, setRange] = useState<RangeValue>('30');
  const [sort, setSort] = useState('sessions');
  const [platform, setPlatform] = useState('');
  // In the fragment, like the other three sections (§5) — "look at this game's numbers" is a thing
  // to paste into a message, and until now this was the one detail view that could not be.
  const [selected, setSelected] = useSelection('analytics');
  // Opened from a row's "failed", so the modal starts at the failures rather than the totals.
  const [toFailures, setToFailures] = useState(false);

  // Counted back from today in UTC, the day the server's range ends at by default: a start counted
  // any other way is a range of some other length.
  const picked = RANGES.find((one) => one.value === range) ?? RANGES[3];
  const from = utcDay(Date.now() - picked.from * 86_400_000);
  const to = utcDay(Date.now() - picked.to * 86_400_000);
  const state = useAsync(
    () =>
      api<AdminAnalyticsResponse>(
        `/v1/admin/analytics?from=${from}&to=${to}&sort=${sort}&limit=100${platform ? `&platform=${platform}` : ''}`,
      ),
    [from, to, sort, platform],
  );
  const selectedRow = state.data?.items.find((row) => row.appId === selected);

  return (
    <>
      <Panel
        title="All games"
        actions={
          <div className="filters">
            <Filter label="Range" value={range} onChange={(next) => setRange(next as RangeValue)} options={RANGES} />
            <Filter label="Platform" value={platform} onChange={setPlatform} options={PLATFORMS} />
            <label className="filter">
            <span className="muted small">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="sessions">Most sessions</option>
              <option value="playtime">Most playtime</option>
              <option value="boards">Most Boards</option>
              <option value="returning">Most kept</option>
              <option value="installers">Most installs</option>
              <option value="name">By name</option>
            </select>
            </label>
          </div>
        }
      >
        <Async state={state} empty="No game has been played or installed in this range.">
          {(data) => (
            <>
              <div className="stats">
                <Stat label="Games" value={String(data.total)} />
                <Stat
                  label="Sessions"
                  value={data.totals.sessions.toLocaleString()}
                  trend={trend(data.totals.sessions, data.totals.previousSessions)}
                />
                <Stat label="Playtime" value={duration(data.totals.playtimeMs)} />
                <Stat
                  label="Avg session"
                  value={data.totals.sessions ? duration(data.totals.playtimeMs / data.totals.sessions) : '—'}
                />
                {/* Boards that hold a game, not Boards that played one: nothing ties a play to a
                    Board (analytics-plan §14.26(d)). */}
                <Stat label="Boards" value={data.totals.boards.toLocaleString()} />
                <Stat label="Accounts installed" value={data.totals.installers.toLocaleString()} />
                <Stat
                  label="Failed installs"
                  value={data.totals.installFailures.toLocaleString()}
                  tone={data.totals.installFailures > 0 ? 'error' : undefined}
                />
              </div>
              {data.excludedAccounts > 0 ? (
                <p className="muted small">
                  Leaves out{' '}
                  <a href="#accounts?excluded=1">
                    {data.excludedAccounts === 1 ? '1 account' : `${data.excludedAccounts} accounts`} excluded
                    from analytics
                  </a>
                  , here and on every team's dashboard. Plays are not tied to accounts, so theirs still count.
                </p>
              ) : null}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th className="num" title="Boards a BoardFlight desktop app found it on; kept = still there on a later day than it arrived. None for a game with analytics off, or installed only by hand.">Boards</th>
                      <th className="num" title="Against the same number of days just before">Sessions</th>
                      <th className="num">Avg session</th>
                      <th className="num">Playtime</th>
                      <th className="num" title="Accounts whose BoardFlight desktop app installed it, an update not counted; failures a Board reported. An install by hand is not counted.">Installs</th>
                      <th>Last played</th>
                      <th>Collecting</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <GameRow
                        key={row.appId}
                        row={row}
                        onOpen={(failures) => {
                          setToFailures(failures);
                          setSelected(row.appId);
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Never a silent top-N: a truncated table that says nothing reads as "this is
                  everything", and an admin concluding nobody uses the product should not be
                  looking at one. */}
              {data.total > data.items.length ? (
                <p className="note">
                  Showing {data.items.length} of {data.total} games played or installed {rangeWords(data.from, data.to)}.
                </p>
              ) : (
                <p className="note">
                  {data.total} game{data.total === 1 ? '' : 's'} played or installed {rangeWords(data.from, data.to)}.
                </p>
              )}
            </>
          )}
        </Async>
      </Panel>

      <Failures from={from} to={to} platform={platform} onOpenGame={(appId) => {
        setToFailures(true);
        setSelected(appId);
      }} />

      <EventIngest from={from} to={to} />

      {selected ? (
        <Game
          appId={selected}
          from={from}
          to={to}
          // The name off the row that was clicked, and the id when the fragment was pasted in
          // cold — the ranking is a range away from being guaranteed to hold this game at all.
          name={selectedRow?.name ?? selected}
          failuresFirst={toFailures}
          onClose={() => {
            setToFailures(false);
            setSelected(null);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The change from the previous range, as a signed percentage — or null when there is nothing to
 * compare with. "New" when the previous range was empty: +∞% says nothing.
 */
export function trend(now: number, before: number): { label: string; tone: 'up' | 'down' | 'flat' } | null {
  if (before === 0) return now > 0 ? { label: 'new', tone: 'up' } : null;
  const change = Math.round(((now - before) / before) * 100);
  if (change === 0) return { label: '±0%', tone: 'flat' };
  return { label: `${change > 0 ? '+' : ''}${change}%`, tone: change > 0 ? 'up' : 'down' };
}

function Trend({ value }: { value: ReturnType<typeof trend> }) {
  if (!value) return null;
  return <span className={`trend ${value.tone}`}>{value.label}</span>;
}

function Stat({ label, value, trend: change, tone }: {
  label: string;
  value: string;
  trend?: ReturnType<typeof trend>;
  tone?: 'error';
}) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className={tone ? `stat-value ${tone}` : 'stat-value'}>
        {value} {change ? <Trend value={change} /> : null}
      </span>
    </div>
  );
}

function GameRow({ row, onOpen }: { row: AdminAppAnalytics; onOpen: (failures: boolean) => void }) {
  const share = row.boards ? Math.round((row.returningBoards / row.boards) * 100) : 0;
  return (
    <tr>
      <td>
        {row.name}
        {row.archived ? <span className="pill" style={{ marginLeft: 6 }}>archived</span> : null}
        <div className="muted small">{row.teamName}</div>
        <div className="mono muted">{row.packageName}</div>
      </td>
      <td className="num">
        {row.boards || '—'}
        {row.boards ? <div className="muted small">{row.returningBoards} kept · {share}%</div> : null}
      </td>
      <td className="num">
        {row.sessions || '—'}
        <div className="small"><Trend value={trend(row.sessions, row.previousSessions)} /></div>
      </td>
      <td className="num">{row.sessions ? duration(row.playtimeMs / row.sessions) : '—'}</td>
      <td className="num">{row.sessions ? duration(row.playtimeMs) : '—'}</td>
      <td className="num">
        {row.installers || '—'}
        {row.installFailures ? (
          <div>
            <button className="link small error" onClick={() => onOpen(true)} title="What the Boards said">
              {row.installFailures} failed
            </button>
          </div>
        ) : null}
      </td>
      <td className="muted small"><Time at={row.lastPlayedAt} /></td>
      <td>
        {/* An empty row means two different things — switched off, versus on and nobody has
            played — and the table has to say which. */}
        <span className={row.collecting ? 'pill on' : 'pill off'}>{row.collecting ? 'on' : 'off'}</span>
      </td>
      <td>
        <button onClick={() => onOpen(false)}>Open</button>
      </td>
    </tr>
  );
}

/**
 * One game's analytics, in a modal.
 *
 * The ranking above is the thing being read, and a game is opened *out of* it — usually more than
 * one, in a row, comparing. Pushing the table down the page each time turns "look at the top four"
 * into four scrolls back up, so the detail goes over the list rather than under it.
 *
 * Three things, each an answer: the totals, which builds its Boards are on, and why installs
 * failed. The table by install id it used to have is gone (2026-10-28): on a web game every launch
 * is a new id, so it was a list of launches with nothing to act on.
 */
function Game({
  appId,
  from,
  to,
  name,
  failuresFirst,
  onClose,
}: {
  appId: string;
  /** The range picker's first and last days, so the modal reads the same days as the row it was opened from. */
  from: string;
  to: string;
  name: string;
  /** Opened from the row's failures: those first. */
  failuresFirst: boolean;
  onClose: () => void;
}) {
  const summary = useAsync(
    () => api<AnalyticsSummary>(`/v1/admin/apps/${appId}/analytics/summary?from=${from}&to=${to}`),
    [appId, from, to],
  );
  const failures = <GameFailures appId={appId} from={from} to={to} />;

  return (
    <Modal title={name} onClose={onClose}>
      {failuresFirst ? failures : null}
      <Panel title="Totals">
        <Async state={summary}>
          {(data) => (
            <div className="row">
              <span>{data.totals.sessions} sessions</span>
              <span className="muted">·</span>
              <span>{duration(data.totals.playtimeMs)} played</span>
              {/* No install count: it is the layer's install ids, which on a web game is the
                  number of launches. The Boards column behind this modal is the real one. */}
              <span className="muted">·</span>
              <span>median {duration(data.totals.medianSessionMs)}</span>
              {!data.enabled ? <span className="pill off">collection off</span> : null}
            </div>
          )}
        </Async>
      </Panel>
      <Versions appId={appId} />
      {failuresFirst ? null : failures}
    </Modal>
  );
}

/** One build, and the Boards on it now. */
export interface VersionShare {
  key: string;
  label: string;
  channel: string | null;
  boards: number;
}

/**
 * The game's Boards by the build they are on now, most first — the same grouping the creator
 * dashboard's channel Activity draws. A removed copy is on no build. Pure, so it is pinned without
 * a DOM.
 */
export function versionShares(items: readonly AdoptionEntry[]): VersionShare[] {
  const groups = new Map<string, VersionShare>();
  for (const entry of items) {
    if (entry.uninstalledAt !== null) continue;
    const key = entry.buildId ?? (entry.versionCode !== null ? `code:${entry.versionCode}` : 'unknown');
    const label =
      entry.buildVersionName !== null
        ? `${entry.buildVersionName}${entry.buildNumber !== null ? ` #${entry.buildNumber}` : ''}`
        : entry.versionCode !== null
          ? `(${entry.versionCode}) unknown build`
          : 'version not reported';
    const group = groups.get(key) ?? { key, label, channel: entry.channelSlug, boards: 0 };
    group.boards += 1;
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => b.boards - a.boards);
}

function Versions({ appId }: { appId: string }) {
  const state = useAsync(() => api<AdoptionResponse>(`/v1/admin/apps/${appId}/analytics/adoption`), [appId]);
  return (
    <Panel title="Versions on Boards">
      <Async state={state}>
        {(data) => {
          const shares = versionShares(data.items);
          const total = shares.reduce((sum, one) => sum + one.boards, 0);
          const removed = data.items.length - total;
          if (!data.enabled) return <p className="muted">Analytics is off on every channel, so no Board is read.</p>;
          if (shares.length === 0) return <p className="muted">No Board a desktop app reads has it now.</p>;
          return (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Build</th>
                      <th>Channel</th>
                      <th className="num">Boards</th>
                      <th className="num">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shares.map((one) => (
                      <tr key={one.key}>
                        <td>{one.label}</td>
                        <td className="mono muted">{one.channel ?? '—'}</td>
                        <td className="num">{one.boards}</td>
                        <td className="num">{Math.round((one.boards / total) * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="note">
                {total} Board{total === 1 ? '' : 's'} a desktop app read
                {removed > 0 ? `, and ${removed} that removed it` : ''}.
              </p>
            </>
          );
        }}
      </Async>
    </Panel>
  );
}

function GameFailures({ appId, from, to }: { appId: string; from: string; to: string }) {
  const state = useAsync(
    () => api<AdminInstallFailuresResponse>(`/v1/admin/installs/failures?from=${from}&to=${to}&app=${appId}`),
    [appId, from, to],
  );
  return (
    <Panel title="Failed installs">
      <Async state={state}>
        {(data) =>
          data.total === 0 ? (
            <p className="muted">No install failed {rangeWords(data.from, data.to)}.</p>
          ) : (
            <FailureTables data={data} perGame={false} />
          )
        }
      </Async>
    </Panel>
  );
}

/**
 * Every failed install in the range, across games: what the Boards said, most often first. Drawn
 * only when something failed, because a panel of nothing is a panel to scroll past.
 */
function Failures({ from, to, platform, onOpenGame }: {
  from: string;
  to: string;
  platform: string;
  onOpenGame: (appId: string) => void;
}) {
  const state = useAsync(
    () =>
      api<AdminInstallFailuresResponse>(
        `/v1/admin/installs/failures?from=${from}&to=${to}${platform ? `&platform=${platform}` : ''}`,
      ),
    [from, to, platform],
  );
  if (!state.data || state.data.total === 0) return null;
  return (
    <Panel title={`Failed installs (${state.data.total})`}>
      <FailureTables data={state.data} perGame onOpenGame={onOpenGame} />
    </Panel>
  );
}

/** The Board's words, verbatim and selectable — they are what gets pasted into a bug report. */
function BoardMessage({ message }: { message: string | null }) {
  return message === null ? (
    <span className="muted">The Board gave no message</span>
  ) : (
    <code className="board-message">{message}</code>
  );
}

function FailureTables({ data, perGame, onOpenGame }: {
  data: AdminInstallFailuresResponse;
  /** Across games: each reason says which game, and opens it. */
  perGame: boolean;
  onOpenGame?: (appId: string) => void;
}) {
  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>What the Board said</th>
              <th className="num">Times</th>
              <th className="num">Accounts</th>
              {perGame ? <th>Game</th> : null}
              <th>Last</th>
            </tr>
          </thead>
          <tbody>
            {data.reasons.map((reason) => (
              <tr key={reason.message ?? ''}>
                <td><BoardMessage message={reason.message} /></td>
                <td className="num">{reason.failures}</td>
                <td className="num">{reason.accounts}</td>
                {perGame ? (
                  <td>
                    {reason.appId && onOpenGame ? (
                      <button className="link" onClick={() => onOpenGame(reason.appId!)}>{reason.appName}</button>
                    ) : (
                      <span className="muted">{reason.games} games</span>
                    )}
                  </td>
                ) : null}
                <td className="muted small"><Time at={reason.lastAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details>
        <summary className="muted small">
          {data.items.length === data.total ? `All ${data.total}` : `The newest ${data.items.length} of ${data.total}`}, one
          by one
        </summary>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                {perGame ? <th>Game</th> : null}
                <th>Build</th>
                <th>Account</th>
                <th>What the Board said</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id}>
                  <td className="muted small"><Time at={item.at} /></td>
                  {perGame ? <td>{item.appName}</td> : null}
                  <td>
                    {item.versionName ?? '?'}
                    {item.buildNumber !== null ? <span className="muted"> #{item.buildNumber}</span> : null}
                    <div className="mono muted">
                      {item.channelSlug ?? '—'}
                      {item.kind === 'update' ? ' · update' : ''}
                    </div>
                  </td>
                  <td><a href={`#accounts/${item.userId}`}>@{item.username}</a></td>
                  <td><BoardMessage message={item.message} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

/**
 * Custom game events per game (docs/game-events-plan.md §9.5): what each game's layer sent, what
 * was stored and what was dropped. Counts from this database's `event_ingest_daily`; the console
 * cannot read a game's events and is not meant to.
 */
function EventIngest({ from, to }: { from: string; to: string }) {
  const state = useAsync(
    () => api<AdminEventIngestResponse>(`/v1/admin/events/ingest?from=${from}&to=${to}`),
    [from, to],
  );
  return (
    <Panel title="Game events">
      <Async state={state} empty="No game has sent game events in this range.">
        {(data) =>
          data.items.length === 0 ? (
            <p className="note">No game has sent game events {rangeWords(data.from, data.to)}.</p>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th>Team</th>
                      <th className="num">Stored</th>
                      <th className="num">Undeclared</th>
                      <th className="num">Mismatched</th>
                      <th className="num">Over the daily cap</th>
                      <th className="num">Invalid</th>
                      <th className="num" title="Thrown away on the device because its queue was full, as the game's layer reported it">
                        Lost on the device
                      </th>
                      <th>Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <tr key={row.appId}>
                        <td>{row.appName}</td>
                        <td className="muted">{row.teamName}</td>
                        <td className="num">{row.accepted}</td>
                        <td className="num">{row.undeclared}</td>
                        <td className="num">{row.mismatched}</td>
                        <td className="num">{row.droppedCap}</td>
                        <td className="num">{row.droppedInvalid}</td>
                        <td className="num">{row.droppedDevice}</td>
                        <td className="muted">{row.lastAt ? new Date(row.lastAt).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* The totals are the server's own sums over every game, not of the rows above, which
                  stop at two hundred. */}
              <p className="note">
                {data.totals.accepted} stored and {data.totals.dropped} dropped by the server
                {data.totals.droppedDevice ? `, ${data.totals.droppedDevice} lost on the devices,` : ''} across{' '}
                {data.totals.games} game{data.totals.games === 1 ? '' : 's'} {rangeWords(data.from, data.to)}
                {data.totals.games > data.items.length ? ` — the ${data.items.length} that stored the most are listed` : ''}.
              </p>
            </>
          )
        }
      </Async>
    </Panel>
  );
}
